import React, { useEffect, useMemo, useState } from 'react';
import { SystemUpdateAlt } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import LoopIcon from '@mui/icons-material/Loop';
import {
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import dayjs, { Dayjs } from 'dayjs';
import { useDebounceValue } from 'usehooks-ts';
import {
  createGeneratedReport,
  getGeneratedReport,
  useGetAllEmailAnalyticsRecords,
  useGetAllUsers,
  useGetEmailAnalyticsRecordsCount,
} from '@juno/client-api';
import {
  CommunityUser,
  CreateGeneratedReportParams,
  GeneratedReport,
  GeneratedReportTypeEnum,
  JunoUser,
  RoleEnum,
} from '@juno/client-api/model';
import { AutoCompleteUsers, DefaultDatePicker, UserTile } from '@juno/ui';
import { calculateUserSearchFilters, doesFileExist, useBreakpoint, useSettings } from '@juno/utils';
import { EmailCampaignType, EmailCampaignTypeMap } from '../../Emailer/utils/utils';
import AnalyticsTable, { DataRow, Order } from './AnalyticsTable';

const DATA_ROW_KEYS = [
  'user.id',
  'user.first_name',
  'user.last_name',
  'user.company',
  'email',
  'date_sent',
];
const DATA_ROW_ORDER_KEYS = [
  'user_id',
  'user__first_name',
  'user__last_name',
  'user__company',
  'email',
  'date_sent',
];
const EmailAnalytics: React.FC = () => {
  const { site, user, isStaff } = useSettings();
  const { xs, sm } = useBreakpoint();

  // Pagination
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<string>('date_sent');
  const orderKey = useMemo(() => {
    const prefix = order === 'desc' ? '-' : '';
    const keyIndex = DATA_ROW_KEYS.findIndex((value, index) => value === orderBy);
    const key = DATA_ROW_ORDER_KEYS[keyIndex];
    return `${prefix}${key}`;
  }, [order, orderBy]);

  // Filtering
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().startOf('date').subtract(7, 'day'),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('day'));
  const [timeFrame, setTimeFrame] = useState('Last 7 Days');
  const [selectCampaignType, setSelectedCampaignType] = useState(EmailCampaignType.DailyDigest);
  const [selectedUsers, setSelectedUsers] = React.useState<JunoUser[]>([]);
  const userIds = useMemo(() => {
    return selectedUsers.map((user) => user.id);
  }, [selectedUsers]);
  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch] = useDebounceValue(userSearch, 400);
  const userSearchFilter = useMemo(() => {
    if (!debouncedUserSearch) return {};
    return calculateUserSearchFilters(debouncedUserSearch);
  }, [debouncedUserSearch]);
  const filter = useMemo(() => {
    const usersFilter = userIds && userIds.length ? { user_id__in: userIds } : {};
    return {
      date_sent__gte: startDate?.toISOString(),
      date_sent__lte: endDate?.toISOString(),
      ...usersFilter,
    };
  }, [startDate, endDate, userIds]);

  // Download
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [hasSelectionChanged, setHasSelectionChanged] = useState<boolean>(true);

  const { data: availableUsers, isFetching: usersFetching } = useGetAllUsers(
    site?.platform_id || '',
    {
      ...userSearchFilter,
      ...(!isStaff
        ? {
            exclude: {
              role: RoleEnum.NUMBER_2,
            },
          }
        : {}),
    },
    {
      query: {
        enabled: !!debouncedUserSearch,
      },
    },
  );
  const { data: rows, isLoading: recordsLoading } = useGetAllEmailAnalyticsRecords(
    site?.id || '',
    {
      filter: filter,
      offset: page,
      limit: pageSize,
      order: orderKey,
    },
    { query: { enabled: !!site?.id } },
  );
  const { data: recordCount, isLoading: recordCountLoading } = useGetEmailAnalyticsRecordsCount(
    site?.id || '',
    { filter: filter },
    { query: { enabled: !!site?.id } },
  );
  const handleAddUser = (user: JunoUser) => {
    setSelectedUsers((prevState) => [...prevState, user]);
  };
  const handleRemoveUser = (user: JunoUser) => {
    setSelectedUsers((prevState) =>
      prevState.filter((value, index, array) => value.id !== user.id),
    );
  };
  const handleClickDownloadReport = async () => {
    if (!startDate || !endDate) {
      return;
    }
    setHasSelectionChanged(false);
    setIsGeneratingReport(true);

    const reportData: GeneratedReport = {
      type: GeneratedReportTypeEnum.emailsemail_records,
      site_id: site?.id || '',
      user_id: user?.id || '',
      admin_id: null,
    };
    const reportParams: CreateGeneratedReportParams = {
      filter: filter,
      context: { order: orderKey },
    };
    const reportResult = await createGeneratedReport(site?.id || '', reportData, reportParams);

    try {
      const interval = setInterval(async () => {
        const result = await getGeneratedReport(site?.id || '', reportResult?.id || '');
        if (result && result?.report_url && doesFileExist(result.report_url)) {
          setIsGeneratingReport(false);
          clearInterval(interval);
          window.open(result.report_url);
        }
      }, 1000);
    } catch (e) {
      setIsGeneratingReport(false);
      setHasSelectionChanged(false);
    }
  };

  useEffect(() => {
    setHasSelectionChanged(true);
    setPage(0);
  }, [filter, orderKey]);

  return (
    <Card variant='elevation' sx={{ p: 4 }}>
      <Stack rowGap={2}>
        <Typography color='primary.main' variant='h5'>
          {document.title} Email Campaign History
        </Typography>
        <Typography sx={{ mt: 4, mb: 3, ml: 1 }}>
          Choose a campaign type to view corresponding email records or "ALL" to see all site email
          records
        </Typography>
        <DefaultDatePicker
          startDate={startDate}
          endDate={endDate}
          timeFrame={timeFrame}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          setTimeFrame={setTimeFrame}
        />
        <FormControl sx={{ width: xs || sm ? '100%' : '32.2%' }}>
          <InputLabel id='campaign-select-label'>Campaign Type</InputLabel>
          <Select
            aria-label='select campaign type'
            id='campaignSelect'
            name='type'
            value={selectCampaignType}
            label='Campaign Type'
            labelId={'campaign-select-label'}
            onChange={(e) => {
              const campaignType = Object.entries(EmailCampaignType).find(
                ([value, type]) => value === e.target.value,
              );
              if (campaignType) {
                setSelectedCampaignType(campaignType[1]);
              }
            }}
          >
            {Object.entries(EmailCampaignTypeMap)
              .filter(([key, value]) => {
                // Eventually we are going to have more but right now limiting to only daily digest
                if (key === EmailCampaignType.DailyDigest) {
                  return value;
                }
                return false;
              })
              .map(([key, value], index) => (
                <MenuItem key={index} value={key}>
                  {value}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <Grid container rowSpacing={2}>
          <Grid item xs={12} md={6}>
            <Stack>
              <AutoCompleteUsers
                options={availableUsers || []}
                selected={selectedUsers}
                onSubmit={handleAddUser}
                onInputChange={setUserSearch}
                isLoading={usersFetching}
                isLoadingPage={false}
                label={'Choose Users'}
                locked={false}
                noOptionsText={debouncedUserSearch === '' ? 'Search to find users' : ''}
                closeOnSelect={false}
                filteredUsers={userIds}
                showSearchIcon={false}
                size={'medium'}
              />
              {selectedUsers.length > 0 && (
                <Box mt={2}>
                  {selectedUsers
                    ?.sort(
                      (a: CommunityUser, b: CommunityUser) =>
                        -b?.last_name?.localeCompare(a?.last_name),
                    )
                    .map((user: JunoUser) => (
                      <UserTile
                        key={user.id}
                        user={user}
                        email={false}
                        loading={false}
                        handleIconClick={() => handleRemoveUser(user)}
                        icon={<CloseIcon sx={{ fontSize: 16 }} aria-label='Remove admin' />}
                        virtualized={false}
                        hideMenu={true}
                      />
                    ))}
                </Box>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} justifyContent='flex-end' display='flex' flexDirection='column'>
            <Stack direction={'row'} spacing={1} justifyContent='flex-end'>
              <Button
                aria-label={'download report'}
                variant='contained'
                disabled={!hasSelectionChanged || isGeneratingReport}
                endIcon={
                  isGeneratingReport ? (
                    <LoopIcon
                      sx={{
                        marginLeft: '10px',
                        animation: 'spin 2s linear infinite',
                        '@keyframes spin': {
                          '0%': {
                            transform: 'rotate(360deg)',
                          },
                          '100%': {
                            transform: 'rotate(0deg)',
                          },
                        },
                      }}
                    />
                  ) : (
                    <SystemUpdateAlt />
                  )
                }
                onClick={handleClickDownloadReport}
              >
                Download Report
              </Button>
            </Stack>
          </Grid>
        </Grid>
        <AnalyticsTable
          columnHeaders={['User ID', 'First Name', 'Last Name', 'Company', 'Email', 'Date Sent']}
          dataRows={rows as unknown as DataRow[]}
          dataRowKeys={DATA_ROW_KEYS}
          rowCount={recordCount?.count ?? 0}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          isLoading={recordsLoading || recordCountLoading}
        />
      </Stack>
    </Card>
  );
};

export default EmailAnalytics;
